import React, { forwardRef, useCallback, useEffect, useRef, useState } from 'react';
import styled from '@emotion/styled';
import useCombinedRef from '@polestar/component-warehouse-react/hooks/useCombinedRef';
import framesync, { cancelSync } from 'framesync';
import useInView from '@polestar/component-warehouse-react/hooks/useInView';
const EMPTY_ARRAY = [];
export var ImageSequenceLoadStrategies;
(function (ImageSequenceLoadStrategies) {
    ImageSequenceLoadStrategies["none"] = "none";
    ImageSequenceLoadStrategies["all"] = "all";
    ImageSequenceLoadStrategies["loadFirstFrame"] = "loadFirstFrame";
    ImageSequenceLoadStrategies["loadOnScrollPriorityFrames"] = "loadOnScrollPriorityFrames";
})(ImageSequenceLoadStrategies || (ImageSequenceLoadStrategies = {}));
const ImageSequence = forwardRef(({ progress, path, extension = 'jpg', paddedZeros = 3, count, onImageLoad, onFirstDraw, onLoaded, alpha = true, lazyLoad = false, forcePriorityFramesLoad = false, priorityFrames: _priorityFrames = EMPTY_ARRAY, firstFrameIndex, loadStrategy, queryProps, ...props }, ref) => {
    // TODO: problem is that when the path changes the frameSync is not triggered correctly
    const canvasRef = useRef(null);
    const images = useRef([]);
    const context = useRef(null);
    const currentImage = useRef(-1);
    const requestedImage = useRef(-1);
    const imagesLoaded = useRef([]);
    const firstDraw = useRef(false);
    const isScrolled = useRef(false);
    const imagesLoadedInitialized = useRef(false);
    if (lazyLoad === false && loadStrategy === undefined) {
        loadStrategy = ImageSequenceLoadStrategies.all;
    }
    else if (lazyLoad === true && loadStrategy === undefined) {
        loadStrategy = ImageSequenceLoadStrategies.none;
    }
    const priorityFrames = useRef(_priorityFrames);
    useEffect(() => {
        priorityFrames.current = _priorityFrames;
    }, [_priorityFrames]);
    const [observableRef, isIntersected] = useInView({
        motionValue: true,
    });
    const combinedRef = useCombinedRef(canvasRef, ref, observableRef);
    const drawImage = useCallback(() => {
        if (requestedImage.current === -1) {
            return;
        }
        const index = requestedImage.current;
        const ctx = context.current;
        const image = images.current[index];
        if (!ctx ||
            !image ||
            !image.complete ||
            !imagesLoaded.current[index] ||
            currentImage.current === index) {
            return;
        }
        requestedImage.current = -1;
        currentImage.current = index;
        if (!firstDraw.current) {
            ctx.canvas.width = image.width;
            ctx.canvas.height = image.height;
            firstDraw.current = true;
            onFirstDraw === null || onFirstDraw === void 0 ? void 0 : onFirstDraw();
        }
        ctx.drawImage(image, 0, 0);
    }, [onFirstDraw]);
    useEffect(() => {
        firstDraw.current = false;
    }, [path, extension, paddedZeros, count]);
    const imageIndexForProgress = useCallback((progressValue) => Math.min(count - 1, Math.max(0, Math.round(count * progressValue))), [count]);
    const getPriorityIndexes = useCallback((indexes, imageListenersRef, imageErrorListenersRef) => {
        if (!indexes) {
            return;
        }
        for (let i = 0; i < indexes.length; i++) {
            if (imagesLoaded.current[indexes[i]] === true) {
                // already loaded, so skip
                continue;
            }
            if (indexes[i] >= count) {
                continue;
            }
            const img = new Image();
            img.src = `${path}/${`${indexes[i]}`.padStart(paddedZeros, '0')}.${extension}${!!queryProps ? `?${queryProps}` : ''}`;
            const handleLoad = () => {
                imagesLoaded.current[indexes[i]] = true;
            };
            const handleError = () => {
                imagesLoaded.current[indexes[i]] = false;
            };
            img.addEventListener('load', handleLoad);
            imageListenersRef === null || imageListenersRef === void 0 ? void 0 : imageListenersRef.push(handleLoad);
            imageErrorListenersRef === null || imageErrorListenersRef === void 0 ? void 0 : imageErrorListenersRef.push(handleError);
            // used for aligning
            images.current[indexes[i]] = img;
        }
    }, [count, extension, paddedZeros, path, queryProps]);
    const unsubLoadImages = useRef(null);
    const handleImageLoading = useCallback(() => {
        var _a, _b;
        context.current =
            (_b = (_a = canvasRef.current) === null || _a === void 0 ? void 0 : _a.getContext('2d', { alpha, desynchronized: true })) !== null && _b !== void 0 ? _b : null;
        const ctx = context.current;
        if (!ctx) {
            return;
        }
        unsubscribeLoadedImages();
        const loadImages = (loadStrategy !== ImageSequenceLoadStrategies.loadFirstFrame &&
            loadStrategy !== ImageSequenceLoadStrategies.loadOnScrollPriorityFrames &&
            !lazyLoad) ||
            isIntersected.get();
        imagesLoadedInitialized.current = loadImages;
        // Reset current
        images.current = new Array(count);
        let imageListenersRef = [];
        let imageErrorListenersRef = [];
        currentImage.current = -1;
        requestedImage.current = -1;
        if (canvasRef === null || canvasRef === void 0 ? void 0 : canvasRef.current) {
            canvasRef.current.dataset.ready = 'false';
        }
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        let loadedImages = 0;
        // Load the current index for progress first
        const currentIndex = imageIndexForProgress(progress.get());
        let priorityIndexes;
        // When using loadStrategy loadFirstFrame, there are 2 levels of priority.
        // We want to preload the first frame only, and the priorityFrames later
        // when the ImageSequence is in view
        let delayedPriorityIndexes;
        if (loadStrategy === ImageSequenceLoadStrategies.loadOnScrollPriorityFrames) {
            priorityIndexes = [currentIndex, ...priorityFrames.current];
        }
        else if (loadStrategy === ImageSequenceLoadStrategies.loadFirstFrame) {
            if (firstFrameIndex) {
                priorityIndexes = [firstFrameIndex];
            }
            else {
                priorityIndexes = [currentIndex];
            }
            delayedPriorityIndexes = [...priorityFrames.current];
        }
        else {
            priorityIndexes = [currentIndex, ...priorityFrames.current];
        }
        if (forcePriorityFramesLoad) {
            getPriorityIndexes(priorityIndexes, imageErrorListenersRef, imageErrorListenersRef);
            getPriorityIndexes(delayedPriorityIndexes, imageErrorListenersRef, imageErrorListenersRef);
        }
        if (!forcePriorityFramesLoad &&
            loadStrategy === ImageSequenceLoadStrategies.loadFirstFrame &&
            isScrolled.current === true) {
            // ensure on scroll the priority frames are loaded
            getPriorityIndexes(delayedPriorityIndexes, imageErrorListenersRef, imageErrorListenersRef);
        }
        // preload image
        for (let index = 0; index < count; index++) {
            if (imagesLoaded.current[index] === true) {
                // already loaded (as priority index for example)
                // provide correct index to draw on the canvas
                requestedImage.current = index;
                continue;
            }
            if (!loadImages && priorityIndexes.indexOf(index) === -1) {
                continue;
            }
            const img = new Image();
            img.src = `${path}/${`${index}`.padStart(paddedZeros, '0')}.${extension}${!!queryProps ? `?${queryProps}` : ''}`;
            const handleLoad = () => {
                imagesLoaded.current[index] = true;
                loadedImages++;
                onImageLoad === null || onImageLoad === void 0 ? void 0 : onImageLoad(index);
                const filteredImagesLoaded = imagesLoaded.current.filter((val) => val === true || val === false);
                if (filteredImagesLoaded.length === count) {
                    if (canvasRef === null || canvasRef === void 0 ? void 0 : canvasRef.current) {
                        canvasRef.current.dataset.ready = 'true';
                    }
                    onLoaded === null || onLoaded === void 0 ? void 0 : onLoaded();
                }
                if (currentImage.current === -1 && imageIndexForProgress(progress.get()) === index) {
                    requestedImage.current = index;
                    // Force drawing the image
                    if (!loadImages) {
                        framesync.render(drawImage);
                    }
                }
                img.removeEventListener('load', handleLoad);
                img.removeEventListener('error', handleError);
            };
            const handleError = () => {
                imagesLoaded.current[index] = false;
                img.removeEventListener('load', handleLoad);
                img.removeEventListener('error', handleError);
            };
            images.current[index] = img;
            imageListenersRef.push(handleLoad);
            imageErrorListenersRef.push(handleError);
            if (img.complete) {
                handleLoad();
            }
            else {
                img.addEventListener('load', handleLoad);
                img.addEventListener('error', handleError);
            }
        }
        const latestImages = images.current;
        unsubLoadImages.current = () => {
            cancelSync.render(drawImage);
            // remove all used event listeners to prevent reloading old references
            latestImages.map((item, index) => {
                item.removeEventListener('load', imageListenersRef[index]);
                item.removeEventListener('error', imageErrorListenersRef[index]);
                // Set src on empty image, which cancels the load of the image
                item.src = 'data:image/gif;base64,R0lGODlhAQABAAAAACH5BAEAAAAALAAAAAABAAEAAAI=';
            });
            imageListenersRef = [];
            imageErrorListenersRef = [];
        };
    }, [
        alpha,
        count,
        drawImage,
        extension,
        imageIndexForProgress,
        isIntersected,
        lazyLoad,
        loadStrategy,
        firstFrameIndex,
        onImageLoad,
        onLoaded,
        paddedZeros,
        path,
        progress,
        queryProps,
        getPriorityIndexes,
        forcePriorityFramesLoad,
    ]);
    const unsubscribeLoadedImages = () => {
        if (unsubLoadImages.current) {
            unsubLoadImages.current();
            unsubLoadImages.current = null;
        }
    };
    // cleanup
    useEffect(() => {
        return () => {
            unsubscribeLoadedImages();
            imagesLoaded.current = [];
            isScrolled.current = false;
            cancelSync.render(drawImage);
        };
    }, 
    // ensure that with every implementation change this is cleaned up
    [drawImage, path]);
    useEffect(() => progress === null || progress === void 0 ? void 0 : progress.on('change', (latestProgress) => {
        requestedImage.current = imageIndexForProgress(latestProgress);
    }), [count, drawImage, imageIndexForProgress, progress]);
    // Load priorityFrames only on scrolling,
    // when selected loadStrategy is loadOnScrollPriorityFrames
    const [loadedPriorityFrames, setLoadedPriorityFrames] = useState(false);
    useEffect(() => {
        const handleScrollLoad = () => {
            isScrolled.current = true;
            if (!loadedPriorityFrames) {
                handleImageLoading();
                setLoadedPriorityFrames(true);
                window.removeEventListener('scroll', handleScrollLoad);
            }
        };
        function watchScroll() {
            window.addEventListener('scroll', handleScrollLoad);
        }
        if ((loadStrategy === ImageSequenceLoadStrategies.loadOnScrollPriorityFrames ||
            loadStrategy === ImageSequenceLoadStrategies.loadFirstFrame) &&
            !loadedPriorityFrames) {
            watchScroll();
        }
    }, [loadedPriorityFrames, loadStrategy, handleImageLoading]);
    useEffect(() => {
        if ((loadStrategy !== ImageSequenceLoadStrategies.none || loadStrategy === undefined) &&
            loadStrategy !== ImageSequenceLoadStrategies.loadOnScrollPriorityFrames) {
            handleImageLoading();
        }
        if (isIntersected.get()) {
            framesync.render(drawImage, true);
        }
        return isIntersected.on('change', (latestIntersected) => {
            if (latestIntersected) {
                if (!imagesLoadedInitialized.current) {
                    handleImageLoading();
                }
                framesync.render(drawImage, true);
            }
            else {
                cancelSync.render(drawImage);
            }
        });
    }, [drawImage, handleImageLoading, isIntersected, lazyLoad, loadStrategy]);
    return React.createElement(SequenceContainer, { ref: combinedRef, ...props });
});
ImageSequence.displayName = 'ImageSequence';
export default ImageSequence;
const SequenceContainer = styled.canvas `
  width: 100%;
  height: 100%;
  object-fit: contain;
`;
